import type {
  LngLat,
  Shape,
} from "../adapters/types";
import { pointInPolygon } from "./pathfinding";

/** `vanish` : au bout du trajet, sur une porte par exemple, l'objet disparaît puis repart du début. */
export type DecorPathEnd = "restart" | "pingpong" | "loop" | "vanish";

/** Ce que porte une forme `shapeKind: "decor"` en plus de sa position (`Shape.coordinates[0]`). */
export interface ShapeDecor {
  model: string;
  rotationDeg: number;
  scale: number;
  elevation?: number;
  /** Fait tourner l'animation du modèle à l'envers — les marches d'un escalator descendent. */
  reverse?: boolean;
  /** Dimensions en mètres d'un modèle construit par le moteur (`DecorModel.procedural`). */
  width?: number;
  length?: number;
  /** Dénivelé en mètres d'un escalier construit par le moteur. Absent : celui que donne sa longueur. */
  depth?: number;
  /** Hauteur en mètres d'un massif rocheux construit par le moteur. Absente : `DecorModel.defaultHeight`. */
  height?: number;
  /** L'escalier débouche sur une zone en contrebas (`Shape.depth`) : ni paroi ni garde-corps en bas. */
  openBottom?: boolean;
  /** Pente en degrés d'un escalier posé à l'air libre. Absente : celle des marches standard. */
  slopeDeg?: number;
  /** Intensité de la fumée, de 0 à 1, d'un modèle qui en émet (`DecorModel.smokes`). */
  smoke?: number;
  /** Image affichée par un modèle qui en porte une : l'écran du fond d'une scène. */
  image?: string;
  path?: LngLat[];
  speedKmh?: number;
  pathEnd?: DecorPathEnd;
}

export interface DecorItem extends ShapeDecor {
  id: string;
  position: LngLat;
}

export interface DecorModelBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  height: number;
}

export interface DecorPose {
  position: LngLat;
  rotationDeg: number;
  moving: boolean;
  /** Entre deux passages d'un trajet `vanish`. */
  hidden?: boolean;
}

export interface DecorGround {
  ring: LngLat[];
  holes?: LngLat[][];
  top: number;
  /** Le toit d'un volume : il l'emporte sur ce qu'il recouvre, fond d'une zone creusée compris. */
  roof?: boolean;
  west: number;
  east: number;
  south: number;
  north: number;
}

const METERS_PER_DEGREE = 111320;
/** Au-delà, un volume est un bâtiment ou un mur, pas un sol sur lequel on pose quelque chose. */
export const DECOR_GROUND_MAX_HEIGHT = 1;
const PATH_ENDS: readonly DecorPathEnd[] = ["restart", "pingpong", "loop", "vanish"];
/** Temps passé hors de vue au bout d'un trajet `vanish`, avant de repartir. */
const VANISH_PAUSE_S = 3;

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function isPair(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isFinite(n));
}

/**
 * Lit `extra.decor` d'une feature. `toLngLat` convertit les points du trajet depuis le système de
 * coordonnées du document, comme pour la géométrie de la forme.
 */
export function parseShapeDecor(raw: unknown, toLngLat: (point: [number, number]) => LngLat): ShapeDecor | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.model !== "string") return null;

  const decor: ShapeDecor = {
    model: value.model,
    rotationDeg: finiteOr(value.rotation, 0),
    scale: finiteOr(value.scale, 1) > 0 ? finiteOr(value.scale, 1) : 1,
  };
  if (typeof value.elevation === "number" && Number.isFinite(value.elevation)) decor.elevation = value.elevation;
  if (value.reverse === true) decor.reverse = true;
  if (value.openBottom === true) decor.openBottom = true;
  if (typeof value.slope === "number" && Number.isFinite(value.slope) && value.slope > 0) decor.slopeDeg = value.slope;
  if (typeof value.smoke === "number" && Number.isFinite(value.smoke)) decor.smoke = Math.max(0, Math.min(1, value.smoke));
  if (typeof value.image === "string" && value.image) decor.image = value.image;
  for (const key of ["width", "length", "depth", "height"] as const) {
    const size = value[key];
    if (typeof size === "number" && Number.isFinite(size) && size > 0) decor[key] = size;
  }

  const path = Array.isArray(value.path) ? value.path.filter(isPair).map(toLngLat) : [];
  if (path.length > 1) {
    decor.path = path;
    decor.speedKmh = Math.max(0.1, finiteOr(value.speed, 5));
    decor.pathEnd = PATH_ENDS.includes(value.pathEnd as DecorPathEnd) ? (value.pathEnd as DecorPathEnd) : "restart";
  }

  return decor;
}

export function serializeShapeDecor(decor: ShapeDecor, fromLngLat: (point: LngLat) => [number, number]): Record<string, unknown> {
  return {
    model: decor.model,
    rotation: decor.rotationDeg,
    scale: decor.scale,
    ...(decor.elevation !== undefined ? { elevation: decor.elevation } : {}),
    ...(decor.reverse ? { reverse: true } : {}),
    ...(decor.width !== undefined ? { width: decor.width } : {}),
    ...(decor.length !== undefined ? { length: decor.length } : {}),
    ...(decor.depth !== undefined ? { depth: decor.depth } : {}),
    ...(decor.height !== undefined ? { height: decor.height } : {}),
    ...(decor.openBottom ? { openBottom: true } : {}),
    ...(decor.slopeDeg !== undefined ? { slope: decor.slopeDeg } : {}),
    ...(decor.smoke !== undefined ? { smoke: decor.smoke } : {}),
    ...(decor.image ? { image: decor.image } : {}),
    ...(decor.path && decor.path.length > 1
      ? { path: decor.path.map(fromLngLat), speed: decor.speedKmh ?? 5, pathEnd: decor.pathEnd ?? "restart" }
      : {}),
  };
}

export function decorItemOfShape(shape: Shape): DecorItem | null {
  if (shape.shapeKind !== "decor" || !shape.decor || !shape.coordinates[0]) return null;
  return { ...shape.decor, id: shape.id, position: shape.coordinates[0] };
}

export function decorShape(item: DecorItem, layer: number): Shape {
  const { id, position, ...decor } = item;
  return {
    id,
    type: "polygon",
    shapeKind: "decor",
    coordinates: [position],
    rotation: 0,
    order: Date.now(),
    layer,
    decor,
  };
}

export function metersToLngLat(origin: LngLat, east: number, north: number): LngLat {
  return [
    origin[0] + east / (METERS_PER_DEGREE * Math.cos((origin[1] * Math.PI) / 180)),
    origin[1] + north / METERS_PER_DEGREE,
  ];
}

export function lngLatToMeters(origin: LngLat, point: LngLat): { east: number; north: number } {
  return {
    east: (point[0] - origin[0]) * METERS_PER_DEGREE * Math.cos((origin[1] * Math.PI) / 180),
    north: (point[1] - origin[1]) * METERS_PER_DEGREE,
  };
}

export function compassBearing(from: LngLat, to: LngLat): number {
  const { east, north } = lngLatToMeters(from, to);
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

export function decorPathLength(item: DecorItem): number {
  const points = pathPoints(item);
  let length = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const { east, north } = lngLatToMeters(points[i]!, points[i + 1]!);
    length += Math.hypot(east, north);
  }
  return length;
}

function pathPoints(item: DecorItem): LngLat[] {
  const path = item.path ?? [];
  return item.pathEnd === "loop" && path.length > 1 ? [...path, path[0]!] : path;
}

export function decorPose(item: DecorItem, elapsedSeconds: number | null): DecorPose {
  const still: DecorPose = { position: item.position, rotationDeg: item.rotationDeg, moving: false };
  if (elapsedSeconds === null || !item.path || item.path.length < 2) return still;

  const points = pathPoints(item);
  const lengths = points.slice(1).map((point, i) => {
    const { east, north } = lngLatToMeters(points[i]!, point);
    return Math.hypot(east, north);
  });
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (total === 0) return still;

  let distance = (elapsedSeconds * (item.speedKmh ?? 5)) / 3.6;
  let reverse = false;
  if (item.pathEnd === "pingpong") {
    distance %= 2 * total;
    if (distance > total) {
      distance = 2 * total - distance;
      reverse = true;
    }
  } else if (item.pathEnd === "vanish") {
    distance %= total + (VANISH_PAUSE_S * (item.speedKmh ?? 5)) / 3.6;
    if (distance > total) {
      return { position: points[points.length - 1]!, rotationDeg: compassBearing(points[points.length - 2]!, points[points.length - 1]!), moving: false, hidden: true };
    }
  } else {
    distance %= total;
  }

  for (let i = 0; i < lengths.length; i++) {
    const length = lengths[i]!;
    if (distance <= length || i === lengths.length - 1) {
      const from = points[i]!;
      const to = points[i + 1]!;
      const ratio = length > 0 ? Math.min(1, distance / length) : 0;
      const bearing = compassBearing(from, to);
      return {
        position: [from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio],
        rotationDeg: reverse ? (bearing + 180) % 360 : bearing,
        moving: true,
      };
    }
    distance -= length;
  }

  return still;
}

/** Un sommet négatif est le fond d'une zone creusée : ce qui s'y trouve y descend. */
export function decorGrounds(volumes: { ring: LngLat[]; holes?: LngLat[][]; top: number }[]): DecorGround[] {
  return volumes
    .filter(({ ring, top }) => ring.length >= 3 && top !== 0 && top <= DECOR_GROUND_MAX_HEIGHT)
    .map(withBounds);
}

/** Les toits des volumes plus hauts qu'une dalle ; sous le niveau du sol pour un volume posé au fond d'une zone creusée. */
export function decorRoofs(volumes: { ring: LngLat[]; holes?: LngLat[][]; top: number }[]): DecorGround[] {
  return volumes
    .filter(({ ring }) => ring.length >= 3)
    .map((volume) => ({ ...withBounds(volume), roof: true }));
}

function withBounds({ ring, holes, top }: { ring: LngLat[]; holes?: LngLat[][]; top: number }): DecorGround {
  return {
    ring,
    ...(holes?.length ? { holes } : {}),
    top,
    west: Math.min(...ring.map((point) => point[0])),
    east: Math.max(...ring.map((point) => point[0])),
    south: Math.min(...ring.map((point) => point[1])),
    north: Math.max(...ring.map((point) => point[1])),
  };
}

export function decorGroundHeight(point: LngLat, grounds: DecorGround[]): number {
  let height = 0;
  let pit: number | null = null;
  let roof: number | null = null;
  for (const ground of grounds) {
    if (ground.roof ? roof !== null && ground.top <= roof : ground.top > 0 && ground.top <= height) continue;
    if (point[0] < ground.west || point[0] > ground.east || point[1] < ground.south || point[1] > ground.north) continue;
    if (!pointInPolygon(point, ground.ring) || ground.holes?.some((hole) => pointInPolygon(point, hole))) continue;
    if (ground.roof) roof = ground.top;
    else if (ground.top < 0) pit = Math.min(pit ?? 0, ground.top);
    else height = ground.top;
  }
  const floor = pit ?? height;
  return roof !== null && roof > floor ? roof : floor;
}
